import {
  Component,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ConfigService } from '../../../services/config.service';
import {
  CustomerCoverageReportType,
  ReportsService,
} from '../../../services/reports.service';
import { DictionaryService } from '../../../services/dictionary.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { PieComponent } from '../../../components/pie/pie.component';
import { ButtonDirective } from '../../../components/button/button.directive';
import { ChartData } from '../../../interfaces/global/chart';

interface CoverageRow {
  'Customer Type': string;
  Count: number;
  Precentage: number;
  Status: string;
}

interface CoverageChart {
  key: string;
  data: ChartData;
}

interface TargetMetRow {
  'Target Met': number;
  'Target Met Precentage': number;
}

interface TableSchemaColumn {
  type: string;
  name: string;
  dataType?: string;
  ignore: boolean;
}

interface TableResponse<TRow> {
  success: boolean;
  message: string;
  data: {
    rows: TRow[];
    filters?: Record<string, string[]>;
    schema?: TableSchemaColumn[];
  };
}

@Component({
  selector: 'app-main-kpis',
  imports: [CommonModule, PieComponent, ButtonDirective],
  templateUrl: './main-kpis.component.html',
  styleUrl: './main-kpis.component.scss',
})
export class MainKpisComponent implements OnInit {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private reportsService = inject(ReportsService);
  private snackbarService = inject(SnackbarService);
  protected dictionary = inject(DictionaryService);

  /**
   * Optional user ID for filtering coverage data.
   * If provided, loads coverage data for specific user.
   * If not provided, loads overall coverage data.
   */
  userId = input<string>();

  loading = signal(true);
  isDownloadingCoveredReport = false;
  isDownloadingUncoveredReport = false;

  readonly targetedFrequencyLabel = 'Targeted frequency (PM)';
  readonly chartSkeletons = Array.from({ length: 5 });
  coverageCharts = signal<CoverageChart[]>([]);
  targetedFrequencyData = signal<ChartData>({
    chartLabel: '',
    itemLabels: [],
    itemValues: [],
  });

  constructor() {
    effect(() => {
      // Track date changes and userId changes
      this.configService.from();
      this.configService.to();
      this.userId();
      void this.loadAllChartsData();
    });
  }

  ngOnInit(): void {}

  /**
   * Loads coverage data from API.
   * Appends userId to endpoint if provided.
   */
  private async loadAllChartsData(): Promise<void> {
    this.loading.set(true);

    const baseUrl = `${environment.apiBaseUrl}/visits/tables/customer-coverage`;
    const coverageEndpoint = this.userId()
      ? `${baseUrl}/${this.userId()}`
      : baseUrl;
    const targetMetEndpoint = this.userId()
      ? `${environment.apiBaseUrl}/visits/tables/target-met/doctor/${this.userId()}`
      : null;

    try {
      const [coverageData, targetMetData] = await Promise.all([
        this.fetchTableData<CoverageRow>(coverageEndpoint, true),
        targetMetEndpoint
          ? this.fetchTableData<TargetMetRow>(targetMetEndpoint)
          : Promise.resolve<TableResponse<TargetMetRow>['data'] | null>(null),
      ]);

      this.buildCoverageChartData(
        coverageData?.rows ?? [],
        coverageData?.filters,
      );

      if (targetMetData) {
        this.buildTargetedFrequencyChartData(targetMetData.rows);
      } else if (!targetMetEndpoint) {
        this.targetedFrequencyData.set({
          chartLabel: this.targetedFrequencyLabel,
          itemLabels: [],
          itemValues: [],
        });
      }
    } finally {
      this.loading.set(false);
    }
  }

  private buildCoverageChartData(
    rows: CoverageRow[],
    filters?: Record<string, string[]>,
  ): void {
    const customerTypes = this.getOrderedValues(
      filters?.['Customer Type'],
      rows.map((row) => row['Customer Type']),
      false,
    );
    const statuses = this.getOrderedValues(
      filters?.['Status'],
      rows.map((row) => row.Status),
    );

    const counts = new Map<string, number>();
    for (const row of rows) {
      const key = this.getCoverageKey(row['Customer Type'], row.Status);
      counts.set(key, (counts.get(key) ?? 0) + (Number(row.Count) || 0));
    }

    this.coverageCharts.set(
      customerTypes.map((customerType) => ({
        key: customerType,
        data: {
          chartLabel: customerType,
          itemLabels: statuses,
          itemValues: statuses.map(
            (status) =>
              counts.get(this.getCoverageKey(customerType, status)) ?? 0,
          ),
        },
      })),
    );
  }

  private getOrderedValues(
    filterValues: string[] | undefined,
    rowValues: string[],
    includeFilterOnlyValues = true,
  ): string[] {
    const validRowValues = rowValues.filter(
      (value): value is string =>
        typeof value === 'string' && value.length > 0,
    );
    const rowValueSet = new Set(validRowValues);
    const orderedFilterValues = (filterValues ?? []).filter(
      (value) =>
        typeof value === 'string' &&
        value.length > 0 &&
        (includeFilterOnlyValues || rowValueSet.has(value)),
    );

    return Array.from(
      new Set([...orderedFilterValues, ...validRowValues]),
    );
  }

  private getCoverageKey(customerType: string, status: string): string {
    return JSON.stringify([customerType, status]);
  }

  private buildTargetedFrequencyChartData(rows: TargetMetRow[]): void {
    const metLabel = 'Met';
    const notMetLabel = 'Not Met';

    const metPercentage = rows.reduce(
      (sum, row) => sum + (row['Target Met Precentage'] ?? 0),
      0,
    );
    const cappedMet = Math.max(0, Math.min(100, metPercentage));
    const notMet = Math.max(0, 100 - cappedMet);

    this.targetedFrequencyData.set({
      chartLabel: this.targetedFrequencyLabel,
      itemLabels: [metLabel, notMetLabel],
      itemValues: [cappedMet, notMet],
      itemDisplayValues: [`${cappedMet.toFixed(1)}%`, `${notMet.toFixed(1)}%`],
    });
  }

  private buildRequestBody(returnAll = false): Record<string, unknown> {
    return {
      ...this.configService.getApiDateTimeRange(),
      page: 1,
      pageSize: 10,
      getTotalCount: 1,
      getAvailableFilters: 1,
      filters: {},
      ...(returnAll ? { returnAll: true } : {}),
    };
  }

  private async fetchTableData<TRow>(
    endpoint: string,
    returnAll = false,
  ): Promise<TableResponse<TRow>['data'] | null> {
    try {
      const response = await lastValueFrom(
        this.http.post<TableResponse<TRow>>(
          endpoint,
          this.buildRequestBody(returnAll),
        ),
      );

      return response.success ? response.data : null;
    } catch {
      this.snackbarService.showError('FailedToLoadData');
      return null;
    }
  }

  onDownloadReport(reportType: CustomerCoverageReportType): void {
    this.setReportDownloading(reportType, true);
    const from = this.configService.from();
    const to = this.configService.to();
    const userId = this.userId();

    this.reportsService
      .getCustomerCoverageReport(reportType, from, to, userId)
      .subscribe({
        next: (response) => {
          this.setReportDownloading(reportType, false);
          if (response.success && response.data?.reportURL) {
            this.reportsService.downloadReport(response.data.reportURL);
          }
        },
        error: () => {
          this.setReportDownloading(reportType, false);
          this.snackbarService.showError('FailedToLoadData');
        },
      });
  }

  private setReportDownloading(
    reportType: CustomerCoverageReportType,
    isDownloading: boolean,
  ): void {
    if (reportType === 'covered') {
      this.isDownloadingCoveredReport = isDownloading;
    } else {
      this.isDownloadingUncoveredReport = isDownloading;
    }
  }
}
